import { useEffect, useRef, useState } from "react";

interface UsePollingOptions {
  /** Interrupteur maître (toggle « Live »). Défaut : true. */
  enabled?: boolean;
  /** Exécute un appel immédiat au montage / à la réactivation. Défaut : true. */
  immediate?: boolean;
}

/**
 * Polling « intelligent » : exécute `callback` toutes les `intervalMs`, mais
 * NE fait rien tant que l'onglet est en arrière-plan (`document.hidden`) — ce
 * qui économise réseau/CPU sur une flotte d'onglets admin ouverts. Au retour au
 * premier plan, un appel immédiat rafraîchit les données.
 *
 * Remplace le `setInterval` brut de l'ancienne page (qui tournait même onglet
 * caché). Le `callback` est gardé dans un ref pour éviter de réarmer le timer à
 * chaque rendu (closure toujours fraîche).
 *
 * @returns `isHidden` : true quand l'onglet est en arrière-plan (pour
 *          l'indicateur « En direct / En pause »).
 */
export function usePolling(
  callback: () => void | Promise<void>,
  intervalMs: number,
  options: UsePollingOptions = {},
): { isHidden: boolean } {
  const { enabled = true, immediate = true } = options;
  const savedCallback = useRef(callback);
  const [isHidden, setIsHidden] = useState<boolean>(
    typeof document !== "undefined" ? document.hidden : false,
  );

  // Toujours pointer sur la dernière version du callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Suivi de la visibilité de l'onglet + refresh immédiat au retour.
  useEffect(() => {
    const onVisibility = () => {
      const hidden = document.hidden;
      setIsHidden(hidden);
      if (!hidden && enabled) {
        void savedCallback.current();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [enabled]);

  // Timer : armé seulement si activé. Les ticks sont ignorés tant que l'onglet
  // est caché (no-op), donc pas de trafic en arrière-plan.
  useEffect(() => {
    if (!enabled) return;
    if (immediate && !document.hidden) {
      void savedCallback.current();
    }
    const id = window.setInterval(() => {
      if (document.hidden) return;
      void savedCallback.current();
    }, intervalMs);
    return () => window.clearInterval(id);
    // immediate est volontairement hors deps : un seul appel immédiat par
    // (ré)activation, pas à chaque changement de intervalMs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs]);

  return { isHidden };
}
